import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { theme } from "../theme.js";
import EventCard from "../components/eventcard.jsx";
import FilterPanel from "../components/FilterPanel.jsx";
import { API_URL } from "../utils/constants.js";
import LoadingOverlay from "../components/LoadingOverlay.jsx";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

export default function EventsPage({ events = [] }) {
  const [searchParams] = useSearchParams();
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState([]);
  const [selectedDistanceFilters, setSelectedDistanceFilters] = useState([]);
  const [minDistance, setMinDistance] = useState("");
  const [maxDistance, setMaxDistance] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(true);


  // Fetch sports from backend
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await fetch(`${API_URL}/api/data/sports`);
        const data = await response.json();
        // Keep the full sport objects with id and name
        setSportsList(data);
      } catch (error) {
        console.error("Error fetching sports:", error);
        // Fall back to default sports if fetch fails
        setSportsList([
          { id: 1, name: "running" },
          { id: 2, name: "cycling" },
          { id: 3, name: "triathlon" },
          { id: 4, name: "hiking" }
        ]);
      }
    };
    fetchSports();
  }, []);
  useEffect(() => {
    let results = [...events];

    // Sort by time (earliest first)
    results.sort((a, b) => {
      const dateA = new Date(a.start_date || 0);
      const dateB = new Date(b.start_date || 0);
      return dateA - dateB;
    });

    // Filter by search query
    if (searchQuery.trim()) {
      results = results.filter((event) => {
        const name = (event.name || "").toLowerCase();
        const location = (event.location || "").toLowerCase();
        const country = (event.country || "").toLowerCase();
        const organizer = (event.organizer?.name || event.organizer || "").toLowerCase();
        const query = searchQuery.toLowerCase();
        return name.includes(query) || location.includes(query) || organizer.includes(query) || country.includes(query);
      });
    }

    // Filter by sports (if ANY sport in races matches ANY selected filter, it's good)
    if (selectedCategoryFilters.length > 0) {
      results = results.filter((event) => {
        // Get sport IDs from races within the event
        let eventSportIds = [];
        if (event.races && Array.isArray(event.races)) {
          event.races.forEach(race => {
            // Race has sport_id field from the database
            if (race.sport_id) {
              eventSportIds.push(race.sport_id);
            }
          });
        }
        
        // Check if ANY event sport ID matches ANY selected filter
        return eventSportIds.some(sportId => 
          selectedCategoryFilters.includes(sportId)
        );
      });
    }

    // Filter by distance ranges and min/max
    // Sum all fields in extra_info that contain "distance" in the label
    if (selectedDistanceFilters.length > 0 || minDistance || maxDistance) {
      results = results.filter((event) => {
        let eventDistances = [];
        
        // Extract and sum all distance fields from races in the event
        if (event.races && Array.isArray(event.races)) {
          event.races.forEach(race => {            
            let totalDistance = 0;
            if (race.extra_info) {
              // Find all keys that contain "distance" (case-insensitive)
              Object.keys(race.extra_info).forEach(key => {
                if (key.toLowerCase().includes('distance')) {
                  const value = parseFloat(race.extra_info[key]);
                  if (!isNaN(value)) {
                    totalDistance += value;
                  }
                }
              });
            }
            eventDistances.push(totalDistance);
          });
        }
        console.log(`Event: ${event.name}, Total Distances: ${eventDistances.join(", ")} km`);
        // Check if total distance matches min/max range
        if (minDistance || maxDistance) {
          const min = minDistance ? parseFloat(minDistance) : 0;
          const max = maxDistance ? parseFloat(maxDistance) : Infinity;
          if (eventDistances.some(distance => distance > min && distance < max)) {
            return true;
          }
        }
        
        return false;
      });
    }

    // Filter by date range
    if (dateRangeStart && dateRangeEnd) {
      const startDate = new Date(dateRangeStart);
      const endDate = new Date(dateRangeEnd);
      results = results.filter((event) => {
        const eventDate = new Date(event.start_date || 0);
        return eventDate >= startDate && eventDate <= endDate;
      });
    }

    // Limit to 25 events
    setFilteredEvents(results.slice(0, 25));
    
    // Only set loading to false when events and all races are fetched
    if (events.length > 0 && events.every(event => event.races && event.races.length > 0)) {
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  }, [events, searchQuery, selectedCategoryFilters, selectedDistanceFilters, minDistance, maxDistance, dateRangeStart, dateRangeEnd, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
  };

  console.log("Filtered Events:", filteredEvents);
  return (
    <>
      {/* Search Section */}
      <section style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <h1 style={styles.searchTitle}>Trouve ta prochaine course</h1>
          
          <form style={styles.searchForm} onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              placeholder="Rechercher des courses, des lieux..."
              value={searchQuery}
              onChange={handleSearch}
              style={styles.searchInput}
            />
            <button type="submit" style={styles.searchButton}>
              <MagnifyingGlassIcon style={{ width: "20px", height: "20px", strokeWidth: 2 }} />
            </button>
          </form>

          {/* Filter Dropdowns */}
          <FilterPanel
            sportsList={sportsList}
            selectedCategories={selectedCategoryFilters}
            onCategoriesChange={setSelectedCategoryFilters}
            selectedDistances={selectedDistanceFilters}
            onDistancesChange={setSelectedDistanceFilters}
            minDistance={minDistance}
            maxDistance={maxDistance}
            onDistanceMinMaxChange={(min, max) => {
              setMinDistance(min);
              setMaxDistance(max);
            }}
            dateRangeStart={dateRangeStart}
            dateRangeEnd={dateRangeEnd}
            onDateRangeChange={(start, end) => {
              setDateRangeStart(start);
              setDateRangeEnd(end);
            }}
          />
        </div>
      </section>

      {/* Races Grid */}
      <main style={styles.page}>
        <h2 style={styles.title}>
          {searchQuery ? `Résultats pour "${searchQuery}"` : "Toutes les courses"}
        </h2>

        {filteredEvents.length === 0 ? (
          <p style={styles.noResults}>Aucune course ne correspond à vos critères.</p>
        ) : (
          <div style={styles.grid}>
            {filteredEvents.map((event) => (
              <EventCard event={event} key={event.id_bdd || event.id || JSON.stringify(event)} />
            ))}
          </div>
        )}
      </main>
      <LoadingOverlay isVisible={loading} message="Chargement des événements..." />;
    </>
  );
}

const styles = {
  searchSection: {
    backgroundColor: theme.colors.bgDark,
    textAlign: "center",
    minHeight: "400px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "100%",
  },
  searchContainer: {
    width: "100%",
    margin: "0 auto",

  },
  searchTitle: {
    fontSize: theme.fonts.size.heroTitle,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textLight,
    margin: `0 0 ${theme.spacing.xxl} 0`,
    lineHeight: "1.2",
    fontStyle: "italic",
  },
  searchForm: {
    display: "flex",
    gap: theme.spacing.md,
    marginBottom: theme.spacing.huge,
    alignItems: "center",
    justifyContent: "center",
  },
  searchInput: {
    flex: 1,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    border: `1px solid ${theme.colors.light}`,
    borderRadius: theme.radius.lg,
    fontSize: theme.fonts.size.md,
    outline: "none",
    backgroundColor: theme.colors.white,
    color: theme.colors.dark,
    transition: `border ${theme.transitions.base}`,
    maxWidth: "700px",
  },
  searchButton: {
    backgroundColor: theme.colors.light,
    border: "none",
    borderRadius: theme.radius.lg,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  page: {
    width: "100%",
    margin: "0 auto",
    padding: `${theme.spacing.huge} ${theme.spacing.xxl}`,
    backgroundColor: theme.colors.bgLight,
    minHeight: "60vh",
  },
  title: {
    fontSize: theme.fonts.size.massive,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
    marginBottom: theme.spacing.xxl,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: theme.spacing.xxl,
  },
  noResults: {
    fontSize: theme.fonts.size.lg,
    color: theme.colors.textMuted,
    textAlign: "center",
    padding: theme.spacing.huge,
  },
};
